import React from "react";
import styled from "styled-components";
import { hexToRgba } from "src/utils/colorUtils";

const VARIANT_TEXT_STYLES = {
  light: {
    titleColor: "#161112",
    textColor: "#161112",
  },
  dark: {
    titleColor: "#ffffff",
    textColor: "#ffffff",
  },
};

const FrostedGlassPanel = ({
  variant = "light",
  highlightColor = "#ffb9c8",
  titleColor,
  textColor,
  bgColor = "rgba(227, 230, 235, 0.18)",
  showLensFlare = true,
  lensFlareVideoSrc = "/videos/lens-flare.mp4",
  children,
}) => {
  const variantTextStyles = VARIANT_TEXT_STYLES[variant] || VARIANT_TEXT_STYLES.light;

  return (
    <GlassBox
      $highlightColor={highlightColor}
      $titleColor={titleColor || textColor || variantTextStyles.titleColor}
      $textColor={textColor || variantTextStyles.textColor}
      $bgColor={bgColor}
    >
      {showLensFlare && (
        <LensFlareVideo autoPlay muted loop playsInline>
          <source src={lensFlareVideoSrc} type="video/mp4" />
        </LensFlareVideo>
      )}
      <GlassShape className="glass-shape--one" />
      <GlassShape className="glass-shape--two" />
      <GlassShape className="glass-shape--three" />
      <Content>{children}</Content>
    </GlassBox>
  );
};

const GlassBox = styled.div`
  position: relative;
  padding: 1.5rem 1rem;
  margin: 0 auto 3.5rem auto;
  background: ${(props) => props.$bgColor || "rgba(227, 230, 235, 0.18)"};
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
  border: 1px solid ${(props) => hexToRgba(props.$highlightColor, 0.5)};
  border-radius: 15px;
  box-shadow:
    0 10px 35px rgba(80, 20, 35, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.32);
  overflow: hidden;
  isolation: isolate;
  max-width: 1200px;
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${(props) => props.$titleColor || "#161112"};
  }
  h3 {
    font-size: 1.4rem;
  }
  h1 strong,
  h2 strong,
  h3 strong,
  h4 strong,
  h5 strong,
  h6 strong {
    color: ${(props) => props.$titleColor || "#161112"};
  }
  p {
    color: ${(props) => props.$textColor || "#161112"};
    font-size: inherit;
    margin-top: 10px !important;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.24) 0%,
      rgba(255, 255, 255, 0.08) 35%,
      rgba(255, 255, 255, 0.02) 100%
    );
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 1px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    pointer-events: none;
    z-index: 5;
  }

  @media (max-width: 980px) {
    padding: 26px 24px;
  }
`;

const GlassShape = styled.span`
  position: absolute;
  pointer-events: none;
  z-index: 2;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.34) 0%,
    rgba(255, 255, 255, 0.14) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.24),
    0 0 22px rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  &.glass-shape--one {
    width: 360px;
    height: 280%;
    left: 33%;
    top: -90%;
    transform: rotate(26deg);
    opacity: 0.42;
  }

  &.glass-shape--two {
    width: 260px;
    height: 270%;
    left: 45%;
    top: -85%;
    transform: rotate(26deg);
    opacity: 0.28;
  }

  &.glass-shape--three {
    width: 180px;
    height: 260%;
    left: 57%;
    top: -80%;
    transform: rotate(26deg);
    opacity: 0.18;
  }

  @media (max-width: 980px) {
    &.glass-shape--one {
      width: 250px;
      height: 270%;
      left: 26%;
      top: -85%;
      opacity: 0.34;
    }

    &.glass-shape--two {
      width: 180px;
      height: 260%;
      left: 42%;
      top: -80%;
      opacity: 0.22;
    }

    &.glass-shape--three {
      width: 115px;
      height: 250%;
      left: 58%;
      top: -75%;
      opacity: 0.14;
    }
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 3;
`;

const LensFlareVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  opacity: 0.15;
  pointer-events: none;
`;

export default FrostedGlassPanel;
